// CRM push + lead pairing actions on the opportunity overview tab.

import { useEffect, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import {
  createLeadFromOpportunity,
  pushOpportunityToCrm,
  transferOpportunityPair,
} from "@/lib/opportunity/opportunityClient";
import {
  fetchLeadTransferCandidates,
  type LeadTransferCandidate,
} from "@/lib/lead/transferLeadOwner";
import { supabase } from "@/lib/supabase";

function displayMember(m: LeadTransferCandidate): string {
  const n = (m.full_name || m.display_name || "").trim();
  if (n) return `${n}${m.email ? ` · ${m.email}` : ""}`;
  return m.email || m.user_id;
}

export function OpportunityLeadCrmPanel(props: {
  opportunity: Opportunity;
  organizationId: string;
  canManageOrgWide: boolean;
  onChanged: () => void;
  setMessage: (msg: { text: string; ok: boolean } | null) => void;
}) {
  const { opportunity, organizationId, canManageOrgWide, onChanged, setMessage } = props;
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<LeadTransferCandidate[] | null>(null);
  const [locations, setLocations] = useState<Array<{ id: string; name: string | null }>>([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [locationId, setLocationId] = useState(opportunity.location_id ?? "");

  useEffect(() => {
    if (!canManageOrgWide || !supabase) return;
    void (async () => {
      const { data } = await supabase
        .from("locations")
        .select("id, name")
        .eq("organization_id", organizationId)
        .order("name");
      setLocations(
        (data ?? []).map((r) => ({
          id: String(r.id),
          name: (r.name as string | null) ?? null,
        })),
      );
    })();
  }, [canManageOrgWide, organizationId]);

  const loadMembers = async () => {
    const { data } = await fetchLeadTransferCandidates(organizationId);
    setMembers(data ?? []);
  };

  const onCreateLead = async () => {
    setBusy(true);
    try {
      const r = await createLeadFromOpportunity(opportunity.id);
      if (!r.ok) {
        setMessage({ text: r.error, ok: false });
        return;
      }
      setMessage({ text: "Draft lead created and linked.", ok: true });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onPushCrm = async (force = false) => {
    setBusy(true);
    try {
      const r = await pushOpportunityToCrm(opportunity.id, force);
      if (!r.ok) {
        setMessage({ text: r.error, ok: false });
        return;
      }
      if (r.data.already_pushed) {
        setMessage({ text: "Already pushed to CRM (use force to re-push).", ok: true });
        return;
      }
      setMessage({
        text: `Pushed to CRM as ${r.data.opp_name ?? r.data.external_ghl_opportunity_id}.`,
        ok: true,
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const onTransferPair = async () => {
    if (!assigneeId && !locationId) {
      setMessage({ text: "Choose a teammate and/or location.", ok: false });
      return;
    }
    setBusy(true);
    try {
      const r = await transferOpportunityPair({
        opportunity_id: opportunity.id,
        assigned_user_id: assigneeId || undefined,
        location_id: locationId || undefined,
      });
      if (!r.ok) {
        setMessage({ text: r.error, ok: false });
        return;
      }
      setMessage({ text: "Lead + opportunity updated.", ok: true });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3 space-y-3">
      <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">CRM &amp; lead pairing</div>

      {!opportunity.lead_id ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void onCreateLead()}
          className="text-sm text-indigo-600 dark:text-indigo-400 underline disabled:opacity-50"
        >
          Create linked lead from opportunity
        </button>
      ) : (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Linked lead: <span className="font-mono">{opportunity.lead_id}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onPushCrm(false)}
          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:opacity-50"
        >
          {opportunity.crm_pushed_at ? "CRM pushed ✓" : "Push to CRM"}
        </button>
        {opportunity.crm_pushed_at ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onPushCrm(true)}
            className="px-2 py-1 rounded border border-gray-300 text-[10px] text-gray-600"
          >
            Re-push
          </button>
        ) : null}
        {opportunity.external_ghl_opportunity_id ? (
          <span className="text-[10px] text-gray-500 font-mono">
            GHL {opportunity.external_ghl_opportunity_id}
          </span>
        ) : null}
      </div>

      {canManageOrgWide ? (
        <div className="border-t border-gray-100 dark:border-zinc-800 pt-3 space-y-2">
          <div className="text-xs font-medium text-gray-700 dark:text-zinc-300">
            Assign user / location (keeps lead in sync)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={assigneeId}
              onFocus={() => void loadMembers()}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-600"
            >
              <option value="">Teammate (optional)</option>
              {(members ?? []).map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {displayMember(m)}
                </option>
              ))}
            </select>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="text-sm border rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-600"
            >
              <option value="">Location (optional)</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name || loc.id}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onTransferPair()}
            className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 text-xs hover:bg-indigo-50 disabled:opacity-50"
          >
            Apply assignment
          </button>
        </div>
      ) : null}
    </div>
  );
}
